import React, { useEffect, useMemo, useState } from 'react';
import { User, Channel, Affiliate, OnlineUser, PresenceStatus, handleAvatarError } from '../types';
import { resolveAvatarUrl } from '../utils/serverUrl';
import { authService } from '../services/authService';
import { getAppVersion } from '../services/appVersion';
import { ChannelListItem, ChannelTypeIcon, channelDisplayName } from './ChannelListItem';
import { ContextMenu } from './ContextMenu';
import { buildChannelMenuItems } from './channelContextMenu';
import { ChannelListSkeleton } from './Skeleton';
import { useLongPress } from '../hooks/useLongPress';
import { findDmPartner, STATUS_LABEL } from '../utils/presence';
import { PresenceDot } from './PresenceDot';
import { AttentionCard } from './home/AttentionCard';
import { OnlineColleaguesCard } from './home/OnlineColleaguesCard';
import { HomeTipCard } from './home/HomeTipCard';
import { PinPickerModal } from './home/PinPickerModal';
import { MiniCalendarCard } from './home/MiniCalendarCard';
import {
  Megaphone, MessageCircle, Pin, Bell,
  ChevronRight, Plus, PenLine,
} from 'lucide-react';

/** 최근 대화 행 수 — 한 줄 배치(모바일·좁은 PC앱·목록 패널) / 넓은 홈 */
const RECENT_LIMIT_STACK = 5;
const RECENT_LIMIT_WIDE = 8;

interface HomeScreenProps {
  currentUser: User;
  channels: Channel[];
  unreadCounts: Record<number, number>;
  onlineUsers?: OnlineUser[];
  onChannelSelect: (channel: Channel) => void;
  /** 헤더 아바타 탭 → 프로필/설정. 미전달 시(데스크톱) 아바타는 장식으로만 표시 */
  onSettingsClick?: () => void;
  /** '지금 대화할 수 있는 동료' 카드의 사람 클릭 → 프로필 */
  onUserClick?: (user: User) => void;
  /** 최근 대화 '전체 보기' → 대화방 목록으로 이동 */
  onGoToChats?: () => void;
  /** 모바일 전용 '새 대화' FAB. 미전달 시 미표시 */
  onCreateChat?: () => void;
  /** 채널 목록을 한 번이라도 받았는지 — false면 빈 문구 대신 스켈레톤 */
  channelsSynced?: boolean;
  /** 칩 관리 메뉴의 고정 해제 + 즐겨찾기 '추가' 창(PinPickerModal). 미전달 시 '추가' 칩도 없다 */
  onTogglePin?: (channel: Channel) => void;
  onToggleMute?: (channel: Channel) => void;
  /** 컨텍스트 메뉴 '대화방 이름 변경' (group만 노출). 입력 모달은 App이 담당 */
  onRenameChannel?: (channel: Channel) => void;
  /** 컨텍스트 메뉴 '채팅방 나가기' (group/dm만 노출). 확인 모달은 App이 담당 */
  onLeaveChannel?: (channel: Channel) => void;
  /** 통합 알림함 열기 (Phase 2) — 미전달 시 벨 아이콘 미표시 */
  onActivityClick?: () => void;
  activityUnread?: number;
  /**
   * 내 접속 상태 — 헤더 아바타 우하단 점. 모바일 홈에서만 쓴다(데스크톱은 rail 아바타가 담당).
   * undefined면 점을 그리지 않는다.
   */
  myStatus?: PresenceStatus;
  /**
   * 블록(확인할 것·최근 대화·일정·공지·즐겨찾기·접속 중인 동료·팁)은 두 배치가 같고 놓는 자리만 다르다.
   * 'stack'(기본) — 한 줄로 쌓는다. 순서는 넓은 홈의 1단(lg 미만)과 같다. 모바일 웹·좁은 PC앱·(예외적으로) 데스크톱 목록 패널.
   *   머리는 벨·내 사진(→설정)이고, 모바일은 '새 대화' FAB가 붙는다.
   * 'wide' — 데스크톱 홈 탭 2컬럼(rail + 홈 한 칸). 머리에 날짜·'대화방 개설', 아래에 바닥글이 붙는다.
   * 동료 검색은 두 배치 모두 두지 않는다(2026-10-23) — 사람 찾기는 온라인 탭·대화방 개설 창이 맡는다.
   */
  layout?: 'stack' | 'wide';
  /** 웹/PWA 모바일 배치(하단 탭바)에서 그리는지 — 팁 문구를 휴대폰용(길게 누르기·설정 탭)으로 고른다 */
  isMobileLayout?: boolean;
  /** '확인할 것'의 '안읽음 보기' — 대화방 탭을 '안읽음' 필터로 연다 */
  onShowUnread?: () => void;
  /** 동료 카드 '전체 보기' → 온라인(조직도) 탭 */
  onOpenOrg?: () => void;
  /** 사용설명서 열기. 장 id를 주면 그 장으로 — 팁 카드·바닥글 */
  onOpenHelp?: (sectionId?: string) => void;
  /** wide 전용: 바닥글 '이용 고지·동의' */
  onOpenRules?: () => void;
  /** 미니 캘린더 '전체 보기' → 캘린더 탭 */
  onOpenCalendar?: () => void;
}

export const HomeScreen: React.FC<HomeScreenProps> = ({
  currentUser,
  channels,
  unreadCounts,
  onlineUsers = [],
  onChannelSelect,
  onSettingsClick,
  onUserClick,
  onGoToChats,
  onCreateChat,
  channelsSynced = true,
  onTogglePin,
  onToggleMute,
  onRenameChannel,
  onLeaveChannel,
  onActivityClick,
  activityUnread = 0,
  myStatus,
  layout = 'stack',
  isMobileLayout = false,
  onShowUnread,
  onOpenOrg,
  onOpenHelp,
  onOpenRules,
  onOpenCalendar,
}) => {
  const isWide = layout === 'wide';
  // 계열사 라벨: 캐시된 목록에서 1회 매칭 (실패해도 홈은 정상 표시)
  const [affiliateLabel, setAffiliateLabel] = useState<string | null>(null);
  useEffect(() => {
    if (!currentUser.affiliateId) return;
    let cancelled = false;
    authService.getAffiliates()
      .then((list: Affiliate[]) => {
        if (cancelled) return;
        const match = list.find(a => a.id === currentUser.affiliateId);
        if (match) setAffiliateLabel(match.label);
      })
      .catch(() => { });
    return () => { cancelled = true; };
  }, [currentUser.affiliateId]);

  // 공지: 공개(읽기전용) 채널 중 가장 최근 글 1건을 띠로 보여준다. 안읽음은 공개 채널 전체 합계
  const noticeChannels = useMemo(
    () => channels
      .filter(ch => ch.type === 'public' && !ch.isArchived)
      .sort((a, b) => (b.lastMessageAt || '').localeCompare(a.lastMessageAt || '')),
    [channels]
  );
  const latestNotice = noticeChannels[0];
  const noticeUnread = useMemo(
    () => noticeChannels.reduce((sum, ch) => sum + (unreadCounts[ch.id] || 0), 0),
    [noticeChannels, unreadCounts]
  );

  // 즐겨찾기 '추가' 창 — 열 때만 마운트한다(열 때마다 검색어가 비어 있게)
  const [pinPickerOpen, setPinPickerOpen] = useState(false);

  // 즐겨찾기: 사용자가 고정한 채널 (고정 최신순)
  const pinnedChannels = useMemo(
    () => channels
      .filter(ch => ch.isPinned && ch.type !== 'self' && !ch.isArchived)
      .sort((a, b) => (b.pinnedAt || '').localeCompare(a.pinnedAt || '')),
    [channels]
  );

  // 최근 대화: 마지막 메시지가 있는 채널 최신순 (공지는 위 섹션과 중복이라 제외)
  const recentLimit = isWide ? RECENT_LIMIT_WIDE : RECENT_LIMIT_STACK;
  const recentChannels = useMemo(
    () => channels
      .filter(ch => ch.lastMessageAt && ch.type !== 'public' && !ch.isArchived)
      .sort((a, b) => (b.lastMessageAt || '').localeCompare(a.lastMessageAt || ''))
      .slice(0, recentLimit),
    [channels, recentLimit]
  );

  // '확인할 것' — 합계는 App의 totalUnread(rail·탭바 대화방 배지)와 같은 식이어야 두 숫자가 갈라지지 않는다
  const unreadSummary = useMemo(() => {
    const rooms = channels
      .filter(ch => (unreadCounts[ch.id] || 0) > 0)
      .sort((a, b) => (b.lastMessageAt || '').localeCompare(a.lastMessageAt || ''));
    return {
      total: channels.reduce((sum, ch) => sum + (unreadCounts[ch.id] || 0), 0),
      rooms: rooms.length,
      names: rooms.slice(0, 3).map(channelDisplayName),
    };
  }, [channels, unreadCounts]);

  // wide 바닥글 버전 — PC앱은 설치 버전, 웹은 빌드 버전이 없으면 '웹'
  const [appVersion, setAppVersion] = useState<string | null>(null);
  useEffect(() => {
    if (!isWide) return;
    let cancelled = false;
    getAppVersion().then(v => { if (!cancelled) setAppVersion(v); }).catch(() => { });
    return () => { cancelled = true; };
  }, [isWide]);

  // 롱프레스/우클릭 액션 메뉴 (self 채널 제외) — 대화방 목록 화면과 동일 UX
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; channel: Channel } | null>(null);
  const handleLongPress = (channel: Channel, pos: { x: number; y: number }) => {
    if (channel.type === 'self') return;
    setCtxMenu({ x: pos.x, y: pos.y, channel });
  };
  const rowActionProps = (onTogglePin || onToggleMute)
    ? { onLongPress: handleLongPress, onContextMenu: handleLongPress }
    : {};

  const channelRow = (ch: Channel) => (
    <ChannelListItem
      key={ch.id}
      channel={ch}
      unreadCount={unreadCounts[ch.id] || 0}
      onSelect={onChannelSelect}
      presenceStatus={ch.type === 'dm' ? findDmPartner(ch, onlineUsers)?.status ?? 'offline' : undefined}
      {...rowActionProps}
    />
  );

  const card = 'bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden';


  // ── 블록: 한 줄 배치(stack)와 넓은 홈(wide)이 같은 조각을 다른 자리에 놓는다 ──

  /** 공지 띠 — 최신 공개 채널 1건. 없으면 빈 안내 */
  const noticeStrip = latestNotice ? (
    <button
      type="button"
      onClick={() => onChannelSelect(latestNotice)}
      className="w-full flex items-center gap-3 bg-brand-100 hover:bg-brand-200/70 active:bg-brand-200 rounded-2xl px-3.5 py-3 text-left transition-colors"
    >
      <span className="w-9 h-9 rounded-xl bg-white flex items-center justify-center text-brand-600 shrink-0">
        <Megaphone size={17} />
      </span>
      <span className="flex-1 min-w-0 flex flex-col">
        <span className="text-[11px] font-bold tracking-wide text-brand-700">
          {channelDisplayName(latestNotice)}{noticeUnread > 0 ? ` · 새 글 ${noticeUnread > 99 ? '99+' : noticeUnread}` : ''}
        </span>
        <span className="text-sm font-semibold text-slate-800 truncate">
          {latestNotice.lastMessage || '아직 올라온 공지가 없습니다'}
        </span>
      </span>
      <ChevronRight size={16} className="text-brand-700 shrink-0" />
    </button>
  ) : (
    <div className={card}>
      <p className="px-4 py-4 text-sm text-slate-400">아직 올라온 공지가 없습니다. 관리자가 올리는 공지가 여기에 표시됩니다.</p>
    </div>
  );

  const pinnedManageHint = window.matchMedia('(pointer: coarse)').matches ? '길게 눌러 관리' : '우클릭으로 관리';
  const pinnedEmptyText = window.matchMedia('(pointer: coarse)').matches
    ? "대화방을 길게 눌러 '채팅방 고정'하면 여기에 표시됩니다."
    : "대화방을 마우스 오른쪽 버튼으로 눌러 '채팅방 고정'하면 여기에 표시됩니다.";

  /** 즐겨찾기 칩 + '추가' 칩 — stack은 가로 스크롤, wide는 줄바꿈 */
  const pinnedChips = (
    <>
      {pinnedChannels.map(ch => (
        <PinnedChip
          key={ch.id}
          channel={ch}
          unread={unreadCounts[ch.id] || 0}
          presenceStatus={ch.type === 'dm' ? findDmPartner(ch, onlineUsers)?.status ?? 'offline' : undefined}
          onSelect={onChannelSelect}
          onLongPress={(onTogglePin || onToggleMute) ? handleLongPress : undefined}
        />
      ))}
      {onTogglePin && (
        <button
          type="button"
          onClick={() => setPinPickerOpen(true)}
          className="w-16 shrink-0 flex flex-col items-center gap-1.5"
          aria-label="즐겨찾기 추가"
          aria-haspopup="dialog"
        >
          <span className="w-14 h-14 rounded-full border-[1.5px] border-dashed border-slate-300 flex items-center justify-center text-slate-400">
            <Plus size={20} />
          </span>
          <span className="text-xs text-slate-400">추가</span>
        </button>
      )}
    </>
  );

  /** 최근 대화 본문 — 비어 있으면 첫 대화를 시작하는 길(온라인 탭)을 안내한다 */
  const recentBody = !channelsSynced ? (
    <ChannelListSkeleton rows={4} size="md" />
  ) : recentChannels.length > 0 ? (
    <div className="divide-y divide-slate-50">{recentChannels.map(channelRow)}</div>
  ) : (
    <p className="px-4 py-4 text-sm text-slate-400">
      아직 나눈 대화가 없습니다. 온라인 탭에서 동료를 찾아 첫 대화를 시작해 보세요.
    </p>
  );

  /** 확인할 것 — 안 읽은 메시지·알림함 개수 */
  const attentionCard = (
    <AttentionCard
      unreadTotal={unreadSummary.total}
      unreadRooms={unreadSummary.rooms}
      unreadRoomNames={unreadSummary.names}
      activityUnread={activityUnread}
      ready={channelsSynced}
      onShowUnread={onShowUnread}
      onOpenActivity={onActivityClick}
    />
  );

  /** 최근 대화 카드 — 머리 안쪽에 '전체 보기'(대화방 탭) */
  const recentSection = (
    <section className={card}>
      <div className="flex items-center justify-between pl-[18px] pr-2.5 pt-3.5 pb-2">
        <h2 className="flex items-center gap-1.5 text-sm font-bold text-slate-600">
          <MessageCircle size={14} className="text-emerald-500" />
          최근 대화
        </h2>
        {onGoToChats && (
          <button
            type="button"
            onClick={onGoToChats}
            className="h-8 pl-2.5 pr-1.5 rounded-lg text-[13px] font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-800 flex items-center gap-0.5 transition-colors"
          >
            전체 보기
            <ChevronRight size={15} aria-hidden="true" />
          </button>
        )}
      </div>
      <div className="border-t border-slate-100">{recentBody}</div>
    </section>
  );

  /** 즐겨찾기 카드 — 칩 배치만 갈린다: stack은 가로 스크롤(카드 가장자리까지), wide는 줄바꿈 */
  const pinnedSection = (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-100 px-[18px] pt-3.5 pb-4">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="flex items-center gap-1.5 text-sm font-bold text-slate-600">
          <Pin size={14} className="text-brand-600" />
          즐겨찾기
        </h2>
        {pinnedChannels.length > 0 && <span className="text-xs text-slate-500">{pinnedManageHint}</span>}
      </div>
      {pinnedChannels.length > 0 || onTogglePin ? (
        <div className={isWide
          ? 'flex flex-wrap gap-3.5'
          : 'flex gap-3.5 overflow-x-auto no-scrollbar -mx-[18px] px-[18px] pt-0.5 pb-1'}
        >
          {pinnedChips}
        </div>
      ) : (
        <p className="text-sm text-slate-400">{pinnedEmptyText}</p>
      )}
    </section>
  );

  /** 즐겨찾기 '추가' 창 — 두 배치 공용. #root로 포털하므로 놓는 자리는 무관하다 */
  const pinPicker = pinPickerOpen && onTogglePin ? (
    <PinPickerModal
      channels={channels}
      onTogglePin={onTogglePin}
      onClose={() => setPinPickerOpen(false)}
    />
  ) : null;

  /** 지금 대화할 수 있는 동료 */
  const colleaguesCard = (
    <OnlineColleaguesCard
      currentUser={currentUser}
      channels={channels}
      onlineUsers={onlineUsers}
      onUserClick={onUserClick}
      onOpenOrg={onOpenOrg}
    />
  );

  /** 알고 계셨나요? — 웹/PWA 모바일 배치만 휴대폰 문구 */
  const tipCard = <HomeTipCard onOpenHelp={onOpenHelp} variant={isMobileLayout ? 'mobile' : 'pc'} />;

  /** 미니 캘린더 — 넓은 홈은 오른쪽 단 맨 위, 한 줄 배치는 최근 대화 다음(넓은 홈 1단 순서와 같다).
   *  데이터는 카드가 calendarService 모듈 캐시로 직접 받는다(두 벌 마운트돼도 요청 1건) */
  const calendarCard = <MiniCalendarCard onOpenCalendar={onOpenCalendar} />;

  /** 롱프레스 액션 메뉴 */
  const contextMenu = ctxMenu && (
    <ContextMenu
      x={ctxMenu.x}
      y={ctxMenu.y}
      variant="light"
      backdrop
      onClose={() => setCtxMenu(null)}
      items={buildChannelMenuItems(
        ctxMenu.channel,
        { onTogglePin, onToggleMute, onRenameChannel, onLeaveChannel },
        () => setCtxMenu(null),
      )}
    />
  );

  // ── 넓은 홈: 데스크톱 홈 탭 2컬럼(rail + 홈 한 칸). 왼쪽 단 = 할 일·내 대화, 오른쪽 단 = 일정·공지·사람·팁 ──
  if (isWide) {
    const affiliateLine = [affiliateLabel, currentUser.depart].filter(Boolean).join(' · ');
    const today = new Intl.DateTimeFormat('ko-KR', { month: 'long', day: 'numeric', weekday: 'long' }).format(new Date());
    const linkButton = 'hover:text-slate-800 hover:underline underline-offset-[3px] transition-colors';
    return (
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-7 pb-8 flex flex-col gap-5">
        {/* 머리: 인사 + 날짜 + 주 액션. 벨은 두지 않는다 — '확인할 것'과 rail 알림 탭이 대신한다 */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3.5 min-w-0">
            <img
              src={resolveAvatarUrl(currentUser.avatar)} onError={handleAvatarError}
              alt=""
              className="w-[52px] h-[52px] shrink-0 rounded-full object-cover bg-slate-100"
            />
            <div className="min-w-0">
              <p className="text-[13px] text-slate-500 truncate">{affiliateLine || ' '}</p>
              <h1 className="mt-0.5 text-2xl font-extrabold tracking-tight text-slate-800 truncate">
                {currentUser.username} 님, 안녕하세요
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <span className="text-sm text-slate-500">{today}</span>
            {onCreateChat && (
              <button
                type="button"
                onClick={onCreateChat}
                className="h-10 pl-3 pr-4 rounded-xl bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white text-sm font-bold flex items-center gap-1.5 transition-colors"
              >
                <Plus size={18} strokeWidth={2.25} aria-hidden="true" />
                대화방 개설
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] items-start gap-5">
          <div className="min-w-0 flex flex-col gap-5">
            {attentionCard}
            {recentSection}
          </div>

          <div className="min-w-0 flex flex-col gap-5">
            {calendarCard}
            {noticeStrip}
            {pinnedSection}
            {colleaguesCard}
            {tipCard}
          </div>
        </div>

        <footer className="flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1 pt-1 text-[13px] text-slate-500">
          <span>INA-TALK {appVersion && /^\d/.test(appVersion) ? appVersion : '웹'}</span>
          {onOpenRules && (
            <>
              <span aria-hidden="true">·</span>
              <button type="button" onClick={onOpenRules} className={linkButton}>이용 고지·동의</button>
            </>
          )}
          {onOpenHelp && (
            <>
              <span aria-hidden="true">·</span>
              <button type="button" onClick={() => onOpenHelp()} className={linkButton}>사용설명서</button>
            </>
          )}
        </footer>

        {contextMenu}
        {pinPicker}
      </div>
    );
  }

  return (
    <div className="px-4 pt-3 pb-24 md:pb-6 flex flex-col gap-5">
      {/* 헤더: 인사 + 알림 + 아바타. 브랜드 레드는 여기서 쓰지 않는다 — 안읽음·주 액션에만 */}
      <div className="flex items-center justify-between gap-3 pt-1">
        <div className="min-w-0">
          <p className="text-xs text-slate-500 truncate">
            {[affiliateLabel, currentUser.depart].filter(Boolean).join(' · ') || ' '}
          </p>
          {/* 이름을 앞에 둔다 — truncate는 뒤를 자르므로 어느 폭에서도 이름이 먼저 살아남는다.
              인사말 꼬리는 폭이 넉넉할 때만(모바일 전체화면·xl 이상 목록 패널) 붙인다 */}
          <h1 className="text-xl font-extrabold text-slate-800 tracking-tight truncate">
            {currentUser.username} 님<span className="md:hidden xl:inline">, 안녕하세요</span>
          </h1>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {onActivityClick && (
            <button
              type="button"
              onClick={onActivityClick}
              className="relative w-11 h-11 md:w-10 md:h-10 flex items-center justify-center rounded-xl text-slate-600 hover:bg-white hover:text-slate-800 transition-colors"
              aria-label={activityUnread > 0 ? `알림, 안읽음 ${activityUnread}` : '알림'}
              title="알림"
            >
              <Bell size={22} />
              {activityUnread > 0 && (
                <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-600 text-white text-[11px] font-bold flex items-center justify-center leading-none">
                  {activityUnread > 99 ? '99+' : activityUnread}
                </span>
              )}
            </button>
          )}
          {/* 내 상태 점은 overflow-hidden 안에 두면 잘리므로 이미지만 감싸고 점은 바깥에 둔다 */}
          {onSettingsClick ? (
            <button
              type="button"
              onClick={onSettingsClick}
              className="relative w-11 h-11 md:w-10 md:h-10 rounded-full ring-2 ring-transparent hover:ring-brand-300 transition-shadow"
              aria-label={myStatus ? `프로필 및 설정, 내 상태 ${STATUS_LABEL[myStatus]}` : '프로필 및 설정'}
              title="프로필 설정"
            >
              <span className="block w-full h-full rounded-full overflow-hidden">
                <img src={resolveAvatarUrl(currentUser.avatar)} onError={handleAvatarError} alt="" className="w-full h-full object-cover bg-slate-100" />
              </span>
              {myStatus && (
                <PresenceDot status={myStatus} className="absolute bottom-0 right-0 w-3 h-3 border-2 border-slate-50" />
              )}
            </button>
          ) : (
            <img src={resolveAvatarUrl(currentUser.avatar)} onError={handleAvatarError} alt="" className="w-11 h-11 md:w-10 md:h-10 rounded-full object-cover bg-slate-100" />
          )}
        </div>
      </div>

      {/* 넓은 홈 1단과 같은 순서 — 할 일 → 내 대화 → 일정 → 공지 → 즐겨찾기 → 사람 → 팁 */}
      {attentionCard}
      {recentSection}
      {calendarCard}
      {noticeStrip}
      {pinnedSection}
      {colleaguesCard}
      {tipCard}

      {/* 새 대화 FAB — 모바일 전용, 앱에서 빨강 채움을 쓰는 유일한 플로팅 버튼 */}
      {onCreateChat && (
        <button
          type="button"
          onClick={onCreateChat}
          className="md:hidden fixed right-5 bottom-[calc(env(safe-area-inset-bottom)+5.25rem)] z-20 h-[52px] pl-4 pr-5 rounded-full bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white text-[15px] font-bold flex items-center gap-2 shadow-lg shadow-brand-600/30 transition-colors"
        >
          <PenLine size={20} aria-hidden="true" />
          새 대화
        </button>
      )}

      {contextMenu}
      {pinPicker}
    </div>
  );
};

/** 즐겨찾기 아바타 칩 — 롱프레스(터치)·우클릭(마우스) 모두 관리 메뉴를 연다 */
const PinnedChip: React.FC<{
  channel: Channel;
  unread: number;
  /** DM 상대의 접속 상태. DM이 아니면 undefined — 점을 그리지 않는다 */
  presenceStatus?: PresenceStatus;
  onSelect: (channel: Channel) => void;
  onLongPress?: (channel: Channel, pos: { x: number; y: number }) => void;
}> = ({ channel, unread, presenceStatus, onSelect, onLongPress }) => {
  const longPress = useLongPress((pos) => onLongPress?.(channel, pos));
  const name = channelDisplayName(channel);
  return (
    <button
      type="button"
      {...(onLongPress ? longPress.handlers : {})}
      onClick={() => onSelect(channel)}
      onContextMenu={(e) => {
        if (!onLongPress) return;
        e.preventDefault();
        onLongPress(channel, { x: e.clientX, y: e.clientY });
      }}
      className="w-16 shrink-0 flex flex-col items-center gap-1.5 select-none"
      aria-label={unread > 0 ? `${name}, 안읽음 ${unread}` : name}
    >
      <span className="relative">
        {channel.type === 'dm' ? (
          <img
            src={resolveAvatarUrl(channel.dmPartnerAvatar)} onError={handleAvatarError}
            alt=""
            className="w-14 h-14 rounded-full object-cover bg-slate-100"
          />
        ) : (
          <span className="block w-14 h-14 rounded-full overflow-hidden [&>span]:w-14 [&>span]:h-14">
            <ChannelTypeIcon type={channel.type} size="lg" />
          </span>
        )}
        {presenceStatus && (
          <PresenceDot
            status={presenceStatus}
            className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 border-2 border-slate-50"
          />
        )}
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1.5 rounded-full bg-brand-600 text-white text-[11px] font-bold flex items-center justify-center border-2 border-slate-50 leading-none">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </span>
      <span className="text-xs font-semibold text-slate-700 truncate w-full text-center">{name}</span>
    </button>
  );
};
